<template>
  <WizzardContainer
    v-if="asset && (transaction || isMultiEntity)"
    :model-value="currentStep"
    :is-open
    :z-index
    :title
    :steps="mappedSteps"
    @close="handleClose"
    @update:model-value="handleStepChange"
  >
    <slot
      :name="`step${currentStep + 1}`"
      :account
      :asset
      :transaction
      :tags
      :entities
    >
      <component
        :is="currentComponent"
        ref="currentComponentRef"
        v-bind="{
          hasExistingAccounts,
          account,
          asset,
          transaction,
          tags,
        }"
        v-model:prices="prices"
        v-model:use-existing-account="useExistingAccount"
        :settings="transactionSettings"
        @update:tags="tags = $event"
      />
    </slot>
    <template #buttons>
      <UIButton
        v-bind="{ disabled }"
        :label="buttonLabel"
        tabindex="11"
        @click="handleButtonClick"
      />
    </template>
    <template
      v-if="loading"
      #loading
    >
      Please wait. <br>We are creating your {{ name }}...
    </template>
  </WizzardContainer>
</template>

<script setup lang="ts">
import {
  computed,
  onMounted,
  provide,
  Ref,
  ref,
  useTemplateRef,
  watch,
} from 'vue'
import { useIntervalFn } from '@vueuse/core'

import {
  Asset,
  AssetClass,
  AssetPriceForm,
  ModalClass,
  StepsNavigation,
  StepsNavigationStatus,
  TransactionClass,
} from '@types'
import {
  InputWizardPreset,
  WizardEntity,
} from '../utils/types'

import { ASSET_CLASS, INVESTMENT_STRATEGY, MULTI_ENTITY_CONTEXT } from '../utils/const'
import { ASSET_FIELD, NOTIFICATION_DELAY } from '@/const'

import { generateRandomKey, getCurrencySymbol } from '@/helpers/common'
import { getAssetListValues, withoutLinkedAccounts } from '@/entities/transactions/components/utils/helpers'
import { getCurrentDate } from '@/helpers/dates'
import {
  addPostfixToAssetTicker,
  createWizardEntity,
  initData,
  processTags,
  resetData,
} from '../utils/helpers'

import { usePowerMode } from '@/hooks/powerMode/powerMode'

import { useNotifications } from '@/plugins/notification'

import useAnalyticsStore from '@/store/analytics'
import { useAssetsBunchStore } from '@/store/assets/bunch'
import { useAssetPricesStore } from '@/store/assets/prices'
import { useRepositoriesStore } from '@/store/repositories/index'
import { useTagsBunchStore } from '@/store/tags/bunch'
import { useTransactionsSettingsStore } from '@/store/transactions/settings'

import { UIButton } from '@ui/buttons'
import StepAccount from '../modules/StepAccount.vue'
import StepData from '../modules/StepData.vue'
import StepValues from '../modules/StepValues.vue'
import StepFinancials from '../modules/StepFinancials.vue'
import StepTags from '../modules/StepTags.vue'
import StepSummary from '../modules/StepSummary.vue'
import WizzardContainer from '../components/WizzardContainer.vue'
import { cloneDeep } from 'lodash'

type Props = {
  title: string
  name: string
  modal: ModalClass<any>

  steps: StepsNavigation[]

  transactionType: string

  accountType?: string
  assetType?: string

  assetClassesList: string[]
  investmentStrategiesList: string[]

  preset?: InputWizardPreset

  disallowExistingAccount?: boolean
  disableValuesStep?: boolean

  allowMultipleEntities?: boolean

  handleBeforeSaveTransaction?: (callback: (item: TransactionClass) => Promise<void>) => Promise<void>

  handleStepStatus?: (index: number) => boolean
}

type Emits = {
  init: []
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const isMultiEntity = computed(
  () => !!props.allowMultipleEntities && !props.preset,
)

const { error, success } = useNotifications()

const { isPowerMode } = usePowerMode()

const analyticsStore = useAnalyticsStore()
const assetsBunchStore = useAssetsBunchStore()
const assetsPricesStore = useAssetPricesStore()
const repositoriesStore = useRepositoriesStore()
const tagsBunchStore = useTagsBunchStore()
const transactionsSettingsStore = useTransactionsSettingsStore()

const { pause, resume } = useIntervalFn(async () => {
  if (
    (await currentComponentRef.value?.nextStepDisallowed?.()) ||
    currentStep.value === summaryStep.value ||
    !mappedSteps.value[currentStep.value].status
  ) {
    pause()
  } else {
    currentStep.value++
  }
}, 10)
pause()

provide('size', 'sm')
provide('accountLabel', props.steps[0].name)
provide('assetLabel', props.steps[1].name)
provide('tagAssetClassList', props.assetClassesList)
provide('tagInvestmentStrategyList', props.investmentStrategiesList)

const currentComponentRef = useTemplateRef<any>('currentComponentRef')

const currentStep = ref(0)
const loading = ref(false)

const useExistingAccount = ref(!props.disallowExistingAccount)

const account = ref<AssetClass>()
const asset = ref<AssetClass>()
const transaction = ref<TransactionClass>()
const prices = ref<AssetPriceForm[]>([])
const tags = ref<Record<string, string | undefined>>({
  [ASSET_CLASS]: undefined,
  [INVESTMENT_STRATEGY]: undefined,
})

// Multiple investing entities — each materializes into its own asset +
// transaction bundle at save time (see handleCreate). Only used when
// `multiEntity` is on; the single-entity path above is untouched.
// Cast past Vue's deep-unwrap of the class instances nested in WizardEntity —
// the ref is reactive at runtime; only the inferred type needs correcting.
const entities = ref<WizardEntity[]>([]) as Ref<WizardEntity[]>

const buildEntity = (useExisting: boolean) =>
  createWizardEntity(
    generateRandomKey(),
    props.transactionType,
    defaultDate.value,
    repositoryId.value,
    currency.value,
    useExisting,
    props.accountType,
  )

const addEntity = () => {
  entities.value.push(buildEntity(hasExistingAccounts.value))
}

const removeEntity = (index: number) => {
  entities.value.splice(index, 1)
}

const seedEntities = () => {
  entities.value = []
  // Start with a single entity; the user adds more as needed.
  addEntity()
}

provide(MULTI_ENTITY_CONTEXT, {
  entities: isMultiEntity.value ? entities : undefined,
  add: addEntity,
  remove: removeEntity,
})

defineExpose({
  setTransaction: (
    callback: (
      transaction: TransactionClass,
      date: string,
      accountId?: string | null,
    ) => void
  ) => {
    callback(transaction.value!, defaultDate.value, accountId.value)
    setTimeout(() => {
      transaction.value?.validateData()
    }, 0)
  },
  entities,
})

const currentComponent = computed(() => {
  switch (currentStep.value) {
    case 0:
      return StepAccount
    case 1:
      return StepData
    case financialsStep.value:
      return StepFinancials
    case valuesStep.value:
      return StepValues
    case tagsStep.value:
      return StepTags
    case summaryStep.value:
      return StepSummary
    default:
      return null
  }
})

const zIndex = computed(() => props.modal.zIndex)
const isOpen = computed(() => props.modal.isOpened)

const defaultDate = computed(() => analyticsStore.getDate || getCurrentDate({}))
provide('defaultDate', defaultDate)

const buttonLabel = computed(() => {
  switch (currentStep.value) {
    case summaryStep.value:
      return `Create ${props.name}`
    default:
      return 'Next'
  }
})

const accountId = computed(() => transaction.value?.entry1().value?.account_id)

const isValidAccount = computed(
  () => (useExistingAccount.value && accountId.value) || account.value?.isValid,
)

const isValidAsset = computed(() => asset.value?.isValid)

const validateTransaction = (transaction: TransactionClass) => {
  const keys = Object.keys(transaction.errors)
  const entry1Title = transaction.settings?.entries[0]?.title
  const entry2Title = transaction.settings?.entries[1]?.title
  return (
    transaction.isValid ||
    keys.every(key =>
      [
        `"${entry1Title}" account`,
        `"${entry1Title}" asset`,
        `"${entry2Title}" asset`,
      ].includes(key),
    )
  )
}

const isValidTransaction = computed(() => {
  if (!transaction.value) return false
  return validateTransaction(transaction.value)
})

const isTagsFilled = computed(() => {
  return Object.values(tags.value).every(tag => tag)
})

// Multi-entity validity — every investing entity must resolve to a valid
// account, and (on the Financials step) carry a date of investment plus a
// commitment when the commitment field is shown.
const isMultiAccountValid = computed(
  () =>
    entities.value.length > 0 &&
    entities.value.every(entity => {
      const accountId = entity.transaction?.entry1()?.value?.account_id
      return (entity.useExisting && accountId) || entity.account.isValid
    })
)

const isMultiTransactionValid = computed(() =>
  entities.value.length > 0 &&
  entities.value.every(entity => validateTransaction(entity.transaction)),
)

const financialsStep = computed(() => isMultiEntity.value ? 
  props.steps.findIndex(step => step.name === 'Financials') : 
  -1
)
const valuesStep = computed(() =>
  props.disableValuesStep || isMultiEntity.value ? -1 : props.steps.length - 3,
)
const tagsStep = computed(() => props.steps.length - 2)
const summaryStep = computed(() => props.steps.length - 1)

const mappedSteps = computed(() => {
  return props.steps.map((item, index) => {
    let status
    switch (index) {
      case 0: {
        const isValid = isMultiEntity.value ? 
          isMultiAccountValid.value : 
          isValidAccount.value
        status = isValid
          ? StepsNavigationStatus.COMPLETED
          : undefined
        break
      }
      case 1: {
        const isValid = isMultiEntity.value ? 
          isValidAsset.value :
          (isValidTransaction.value && isValidAsset.value)
        status = isValid
          ? StepsNavigationStatus.COMPLETED
          : undefined
        break
      }
      case financialsStep.value:
        status = isMultiTransactionValid.value
          ? StepsNavigationStatus.COMPLETED
          : undefined
        break
      case valuesStep.value:
        status = prices.value.length
          ? StepsNavigationStatus.COMPLETED
          : StepsNavigationStatus.SKIPPED
        break
      case tagsStep.value:
        status = isTagsFilled.value
          ? StepsNavigationStatus.COMPLETED
          : undefined
        break
      case summaryStep.value:
        break
      default:
        status = props.handleStepStatus?.(index)
          ? StepsNavigationStatus.COMPLETED
          : StepsNavigationStatus.SKIPPED
        break
    }
    return {
      ...item,
      status,
    }
  })
})

const disabled = computed(() => {
  const status = mappedSteps.value[currentStep.value].status
  return (
    (!status ||
      ![
        StepsNavigationStatus.COMPLETED,
        StepsNavigationStatus.SKIPPED,
      ].includes(status)) &&
    currentStep.value !== mappedSteps.value.length - 1
  )
})

const repositoryId = computed(() => repositoriesStore.getCurrentId)
const currency = computed(() => repositoriesStore.getCurrentCurrency)

const assetCurrency = computed(
  () =>
    asset.value?.field<string>(ASSET_FIELD.CURRENCY).value || currency.value,
)

const currencySymbol = computed(() => getCurrencySymbol(assetCurrency.value))
provide('currency', currencySymbol)

const transactionSettings = computed(() => transactionsSettingsStore.getList.find(setting => setting.name === props.transactionType))

const hasExistingAccounts = computed(() => {
  if (!transactionSettings.value || props.disallowExistingAccount) return false
  const list = Array.from(assetsBunchStore.getList.values()).map(instance =>
    instance.get(),
  )
  const data = isPowerMode?.value ? list : withoutLinkedAccounts(list)
  return !!getAssetListValues(
    transactionSettings.value.entries[0].account,
    data,
  )?.length
})

const handleClose = () => {
  props.modal.close()
}

const resolveCurrencyId = () => {
  const currencyAsset = asset.value?.field<Asset | undefined>(
    ASSET_FIELD.CURRENCY_ASSET,
  ).value
  return (
    currencyAsset?.id ||
    assetsBunchStore.getElementByTicker(assetCurrency.value)?.id
  )
}

const saveFlow = (accountId: string, assetId: string, currencyId?: string) => (item: TransactionClass) => {
  item.pasteAccountId1(accountId, { check: false })
  item.pasteAssetId1(assetId, { check: false })
  currencyId && item.pasteAssetId2(currencyId, { check: false })
  return item.store()
}

const createHolding = async (
  useExistingAccount: boolean,
  account: AssetClass | undefined,
  transaction: TransactionClass,
  asset: AssetClass,
  tagsIds: string[],
  prices?: AssetPriceForm[],
  contributions?: TransactionClass[],
  distributions?: TransactionClass[],
  addAcountNameToTicker?: boolean,
) => {

  const newAsset = new AssetClass(cloneDeep(asset.data), asset.isNew)
  if (tagsIds.length) {
    newAsset.field<string[]>(ASSET_FIELD.TAGS).value = tagsIds
  }

  let entityAccountId = transaction.entry1().value?.account_id
  if (!useExistingAccount && !!account) {
    await account?.store()
    entityAccountId = account.id
    transaction.pasteAccountId1(entityAccountId, { check: false })
  }

  if (addAcountNameToTicker) {
    const account = assetsBunchStore.getElementById(entityAccountId!)
    addPostfixToAssetTicker(newAsset, account)
  }

  await newAsset.store()
  const assetId = newAsset.id

  transaction.pasteAssetId1(assetId, { check: false })

  const currencyId = resolveCurrencyId()
  currencyId && transaction.pasteAssetId2(currencyId, { check: false })

  accountId.value && await props.handleBeforeSaveTransaction?.(saveFlow(accountId.value, assetId, currencyId))

  // create prices if exists
  prices?.length && (await Promise.all(
    prices.map(price =>
      assetId &&
      assetsPricesStore.store({
        ...price,
        asset_id: assetId,
      }),
    ),
  ))
      
  await Promise.all(
    [...(contributions || []), ...(distributions || [])].map(saveFlow(entityAccountId!, assetId, currencyId)),
  )

  await transaction.store()

  // open asset summary
  newAsset?.openDrawer()
}

const handleCreate = async () => {
  if (!asset.value || (!transaction.value && !isMultiEntity.value)) return
  loading.value = true
  try {
    const tagsIds = await processTags(tags.value, tagsBunchStore)
    if (isMultiEntity.value) {
      await Promise.all(
        entities.value.map(entity =>
          createHolding(
            entity.useExisting,
            entity.account,
            entity.transaction,
            asset.value!,
            tagsIds,
            entity.prices,
            entity.contributions,
            entity.distributions,
            true,
          ),
        ),
      )
    } else if (transaction.value) {
      await createHolding(
        useExistingAccount.value,
        account.value,
        transaction.value,
        asset.value,
        tagsIds,
        prices.value,
      )
    }

    await success(
      {
        message: `Your ${props.name} has been successfully created`,
      },
      NOTIFICATION_DELAY,
    )

    handleClose()
  } catch (e) {
    await error({ message: e instanceof Error ? e.message : 'Something went wrong' })
  } finally {
    loading.value = false
  }
}

const handleStepChange = async (value: number) => {
  if (await currentComponentRef.value?.nextStepDisallowed?.()) return
  currentStep.value = value
}

const handleButtonClick = async () => {
  if (await currentComponentRef.value?.nextStepDisallowed?.()) return
  if (currentStep.value === summaryStep.value) {
    handleCreate()
    return
  }
  currentStep.value++
}

watch(
  () => asset.value?.field(ASSET_FIELD.NAME).value,
  value => {
    if (loading.value) return
    asset.value && (asset.value.field(ASSET_FIELD.TICKER).value = value)
  },
)

// Clamp the active step if the steps list shrinks (mode toggle swaps it).
watch(
  () => props.steps.length,
  length => {
    if (currentStep.value > length - 1) currentStep.value = length - 1
  },
)

let hasInitialized = false

watch(
  [isOpen, transactionSettings],
  ([value, transactionSettings]) => {
    if (!repositoryId.value || !transactionSettings) return
    if (!value) {
      currentStep.value = 0
      resetData(account, asset, transaction, prices, tags)
      entities.value = []
      hasInitialized = false
      emit('init')
      return
    }
    // Initialize once per open session. A transactionSettings change while the
    // wizard is open (e.g. the drawdown toggle swaps the transaction type)
    // must not re-run initData/seedEntities — that would wipe the investor
    // list and asset data the user already entered. The transaction-type swap
    // is handled per entity by the owning wizard's isNoDrawdown watcher.
    if (hasInitialized) return
    hasInitialized = true
    initData(
      props.transactionType,
      defaultDate.value,
      asset,
      isMultiEntity.value ? undefined : account,
      isMultiEntity.value ? undefined : transaction,
      prices,
      tags,
      repositoryId.value,
      currency.value,
      props.assetClassesList,
      props.investmentStrategiesList,
      props.accountType,
      props.assetType,
      props.preset,
    )
    if (props.preset) {
      if (!props.disallowExistingAccount) {
        useExistingAccount.value = !props.preset?.account?.isNew
      }
      resume?.()
    } else {
      useExistingAccount.value = !props.disallowExistingAccount
    }
    // Multi-entity wizards (no preset) start the entity-list flow with one row.
    if (isMultiEntity.value) seedEntities()
  },
  { immediate: true },
)

watch(hasExistingAccounts, value => {
  if (value === undefined || props.preset) return
  useExistingAccount.value = value
}, { immediate: true })

onMounted(() => {
  if (!transactionsSettingsStore.initFlag) {
    transactionsSettingsStore.fetch()
  }
})
</script>
